<template>
  <transition
    name="fade"
    mode="out-in">
    <div
      class="container mx-auto px-4 max-w-lg pb-12"
      v-if="product">
      <article>
        <header class="pb-6 mb-4">
          <p class="font-bold text-gray-800 mb-4">
            Fitted quality essentials is the foundation of a stylish wardrobe.
          </p>
          <p class="font-bold text-gray-800 mb-4">
            To ensure you’ll always have the perfect sunglasses at hand. I've created easy, wearable basics for your everyday style.
          </p>
          <p class="font-bold mb-4">
            Introducing the Original Panther sunglasses
          </p>
          <p class="font-bold mb-4">
            Medium size. Made from hand-polished cellulose acetate, the most impact-resistant material for sunglasses.
          </p>
          <p class="font-bold">
            Fitted with polarized mineral glasses for maximum clarity, color and scratch resistance.
          </p>
        </header>

        <div class="video-container mb-3" v-if="false">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/XmQdKTz1tmI"
            frameborder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen />
        </div>

        <figure class="mb-3">
          <img src="~@/assets/images/main-product.jpg" alt="Image showing sun glasses and t-shirt">
        </figure>

        <p class="text-sm block text-gray-600 mb-4" v-if="false">
          <img
            src="~@/assets/images/arrow-up.svg"
            alt="Arrow up"
            class="inline-block mr-px -mt-1"> Watch the story on why I made my own brand
        </p>

        <accordion class="mb-4">
          <template v-slot:header>
            Image Gallery
          </template>
          <template v-slot:content>
            <img
              :src="image.src"
              :alt="image.altText"
              v-for="(image,index) in product.images"
              :key="index"
              width="480"
              height="480"
              class="mb-4 w-full">
          </template>
        </accordion>
        <h1 class="mb-4 text-xl">
          Shopping Cart
        </h1>

        <div
          class="border-t border-gray-300 pt-4"
          v-for="(item,index) in lineItems"
          :key="index">
          <line-item
            :item="item"
            :product="product"
            :is-dirty="isDirty"
            :index="index" />
          <div class="flex">
            <button
              v-if="index > 0"
              @click.prevent="removeItem(index)"
              class="mb-3 text-xs text-gray-800 hover:underline ml-auto">
              Remove
            </button>
          </div>
        </div>

        <div class="mb-4 border-t border-gray-300 pt-4">
          <button
            @click.prevent="addItem"
            class="flex items-center w-full py-2">
            <span class="h-8 w-8 inline-flex items-center justify-center bg-white text-black text-xl p-1 leading-none font-thin mr-2">+</span>
            <span class="block text-left leading-none">
              <span class="block text-lg">Add sunglasses</span>
            </span>
          </button>
        </div>

        <div class="mb-10">
          <div class="flex text-xl border-t border-gray-300 pt-6">
            <div class="w-3/6">
              Subtotal
            </div>
            <div class="ml-auto pl-4">
              NOK {{ subTotal }}
            </div>
          </div>
        </div>
        <div class="my-6">
          <p class="text-lg text-white text-center">
            Free shipping! Taxes are calculated during checkout
          </p>
        </div>
      </article>

      <div
        v-if="!isValid && isDirty"
        class="bg-red-600 text-white p-2 mb-2 rounded-sm text-sm text-center">
        <p>One of your items is missing a size</p>
      </div>

      <default-button
        @click.prevent.native="checkout"
        :loading="submitting"
        primary
        large>
        Checkout
      </default-button>
    </div>
  </transition>
</template>

<script>
import client from "@/shopify"
import Accordion from "@/components/Accordion"
import LineItem from "@/components/LineItem"
import DefaultButton from "@/components/DefaultButton"

export default {
  data () {
    return {
      product: null,
      lineItems: [{
        variantId: null,
        quantity: 1
      }],
      checkoutId: localStorage.getItem("panther_checkout_id"),
      submitting: false,
      isDirty: false
    }
  },

  components: {
    Accordion,
    LineItem,
    DefaultButton
  },

  mounted () {
    client.product.fetch("Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0LzQ4MDg3NTYwMDI5MDE=")
      .then((product) => {
        // Do something with the product
        this.product = product
        this.lineItems[0].variantId = product.variants.find(product => product.available).id
      })
  },

  methods: {
    createCheckout () {
      return client.checkout.create()
    },

    addItem () {
      this.lineItems.push({
        variantId: this.product.variants.find(product => product.available).id,
        quantity: 1
      })
    },

    removeItem (index) {
      this.lineItems.splice(index, 1)
    },

    addLineItems (checkoutId) {
      return client.checkout.addLineItems(checkoutId, this.lineItems)
    },

    checkout () {
      this.isDirty = true

      if (!this.isValid) return

      this.submitting = true
      this.createCheckout()
        .then((checkout) => this.addLineItems(checkout.id))
        .then(checkout => {
          window.location.replace(checkout.webUrl)
        })
        .catch(() => {
          this.submitting = false
        })
    }
  },

  computed: {
    subTotal () {
      return this.lineItems.reduce((total, lineItem) => {
        if (lineItem.variantId === null) return 0

        let prod = this.product.variants.find(variant => variant.id === lineItem.variantId)
        return parseInt(prod.price) + total
      }, 0)
    },

    isValid () {
      let invalidItem = this.lineItems.find(item => item.variantId === null)
      return invalidItem ? false : true
    }
  }
}
</script>

<style lang="scss" scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .video-container {
    position: relative;
    padding-bottom: 62.5%; /*16:10*/
    height: 0;
    overflow: hidden;
  }

  .video-container iframe,
  .video-container object,
  .video-container embed {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
</style>
